/**
 * Live Testing 타입 정의
 *
 * V2 REST API 기반의 Live Testing Preview를 위한 타입들
 */

import { QuestionResponseDto } from '../../../../api/generated/types';

/**
 * Live Testing에서 사용하는 질문 타입
 * QuestionResponseDto를 그대로 사용하거나 확장 가능
 */
export type LiveTestingQuestion = QuestionResponseDto;

/**
 * Select/MultiChoice 타입의 답변 값
 */
export interface LiveTestingSelectValue {
  selectedKeys: string[];
  notesForKeys: Record<string, string>;
}

/**
 * BloodPressure 타입의 답변 값
 */
export interface LiveTestingBloodPressureValue {
  systolic?: number;
  diastolic?: number;
}

/**
 * Frequency 타입의 답변 값
 */
export interface LiveTestingFrequencyValue {
  times?: number;
  period?: string;
}

/**
 * RelativeOnset 타입의 답변 값
 */
export interface LiveTestingRelativeOnsetValue {
  duration?: number;
  unit?: string;
}

/**
 * BodySelect 그룹
 */
export interface LiveTestingBodySelectGroup {
  bodyPart: string;
  selectedOptions: string[];
}

/**
 * Collapsible 타입의 답변 값
 */
export interface LiveTestingCollapsibleValue {
  /** 선택된 아이템 키들 */
  selectedKeys: string[];
  /** 각 아이템별 상세 답변 */
  itemAnswers: Record<string, Record<string, LiveTestingAnswerValue>>;
}

/**
 * MultiTextNested 항목의 값
 */
export interface LiveTestingMultiTextNestedItem {
  id: string;
  text: string;
  selectedOptions: string[] | LiveTestingAnswerValue;
  notesForKeys: Record<string, string> | LiveTestingAnswerValue;
}

/**
 * 답변 값의 유니온 타입
 * V2Qs 컴포넌트 value 타입들과 호환
 */
export type LiveTestingAnswerValue =
  | string
  | number
  | string[]
  | LiveTestingSelectValue
  | LiveTestingBloodPressureValue
  | LiveTestingFrequencyValue
  | LiveTestingRelativeOnsetValue
  | LiveTestingBodySelectGroup[]
  | LiveTestingCollapsibleValue
  | LiveTestingMultiTextNestedItem[]
  | Record<string, unknown>  // 기타 객체 타입 허용
  | null
  | undefined;

/**
 * 모든 질문에 대한 답변 맵
 * questionKey → 답변 값
 */
export type LiveTestingAnswerMap = Record<string, LiveTestingAnswerValue>;

/**
 * inputType에 따른 컴포넌트 매핑 정보
 */
export const INPUT_TYPE_TO_COMPONENT: Record<string, string> = {
  // Text 입력 계열
  text: 'V2QsText',
  textarea: 'V2QsTextarea',
  number: 'V2QsNumber',
  phone_number: 'V2QsPhoneNumber',

  // 선택 계열
  select: 'V2QsSelect',
  multiChoice: 'V2QsSelect',
  radio: 'V2QsSelect',
  select_sheet: 'V2QsSelect', // deprecated
  select_sheet_single: 'V2QsSelect',
  select_sheet_multi: 'V2QsSelect',

  // 날짜/시간 계열
  date: 'V2QsDate',
  date_dob: 'V2QsDateDob',
  datetime: 'V2QsDatetime',
  time: 'V2QsTime',

  // 특수 입력 계열
  range: 'V2QsRange',
  blood_pressure: 'V2QsBloodPressure',
  frequency: 'V2QsFrequency',
  signature: 'V2QsSignature',
  image: 'V2QsImage',

  // 위치 선택 계열
  body_select: 'V2QsBodySelect',
  abd_location: 'V2QsAbdLocation',
  select_abd_location: 'V2QsAbdLocation',

  // 복합 입력 계열
  multi_text: 'V2QsMultiText',
  onset: 'V2QsOnset',
  relative_onset: 'V2QsRelativeOnset',

  // 표시 전용 계열
  stage_milemarker: 'V2QsStageMileMarker',
  markdown: 'Markdown',

  // 컨테이너 계열
  collapsible: 'V2QsCollapsible',
  sx_collect: 'V2QsSxCollector',

  // 증상 선택
  sx_select: 'V2QsSelect',

  // 국가 선택
  country_select: 'V2QsSelectCountry',
  select_country: 'V2QsSelectCountry',
  select_countries: 'V2QsSelectCountry',
};

/**
 * 답변이 필요 없는 표시 전용 inputType 목록
 */
export const DISPLAY_ONLY_INPUT_TYPES = [
  'stage_milemarker',
  'markdown',
];

/**
 * Select 계열 inputType 판별
 */
export const isSelectType = (inputType: string): boolean => {
  return ['select', 'multiChoice', 'radio', 'select_sheet', 'select_sheet_single', 'select_sheet_multi', 'sx_select'].includes(inputType);
};

/**
 * Radio 모드 판별 (단일 선택)
 */
export const isRadioMode = (inputType: string): boolean => {
  return inputType === 'radio';
};
